import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import PricingCards from '../../components/Pricing/PricingCards';

const CONTAINER = 'max-w-6xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';

const FAQS = [
  {
    q: 'How does the free trial work?',
    a: 'Every new manager gets a 14-day free trial — no card required. Add a payment method any time before day 14 to pick a paid plan; otherwise you automatically move to Pay As You Go on day 15 and only pay when a shift is successfully covered.',
  },
  {
    q: 'What is the Founding Member offer?',
    a: 'The first 20 paying Monthly customers lock in £19.99/month for as long as their subscription stays active. Cancel any time.',
  },
  {
    q: 'Do I need a contract?',
    a: 'No. There is no long-term contract on any plan. Cancel any time from Manager → Billing.',
  },
  {
    q: 'How does Pay As You Go work?',
    a: 'You only pay £3.99 per successfully completed shift, billed monthly. There is no monthly minimum — Stripe consolidates every shift you covered that month into one invoice. PAYG is also the automatic fallback if you don\u2019t add a card before your free trial ends.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes. Switch between Pay As You Go, Monthly, Annual and Multi-site any time — pro-rated automatically.',
  },
  {
    q: 'What counts as a "successfully completed shift"?',
    a: 'A shift where a worker started, completed the required till/shift record and was signed off by both sides. Cancelled or no-show shifts never incur a fee.',
  },
];

function FaqRow({ q, a, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 py-6" data-testid={`faq-${id}`}>
      <button
        type="button"
        className="w-full flex items-start justify-between text-left gap-4"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-base sm:text-lg font-semibold text-[#0B132B]">{q}</span>
        <ChevronDown
          className={`h-5 w-5 text-slate-400 shrink-0 mt-1 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="mt-3 text-[14.5px] text-slate-500 leading-relaxed max-w-2xl">{a}</p>
      )}
    </div>
  );
}

export default function PricingPage() {
  return (
    <div data-testid="pricing-page">
      <section className={`${CONTAINER} pt-32 pb-14 lg:pt-40 lg:pb-16 text-center`}>
        <p className={EYEBROW}>Pricing</p>
        <h1 className={`${H1} mt-5 max-w-3xl mx-auto`}>Simple, honest pricing.</h1>
        <p className={`${BODY} mt-6 max-w-xl mx-auto`}>
          Start free. Pay only when a shift is successfully covered — or pick a subscription plan.
        </p>
      </section>

      <section className={`${CONTAINER} pb-16 lg:pb-20`}>
        <PricingCards onCtaClick={() => (window.location.href = '/register?role=manager')} />
      </section>

      <section className={`${CONTAINER} pb-24 lg:pb-32`}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className={EYEBROW}>Frequently asked</p>
            <h2 className={`${H2} mt-3`}>Pricing questions.</h2>
          </div>
          <div>
            {FAQS.map((f, i) => (
              <FaqRow key={i} id={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50/70 py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className={`${H2}`}>Try it free for 14 days.</h2>
          <p className={`${BODY} mt-4`}>No card required. Cancel any time.</p>
          <Link to="/register?role=manager" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors" data-testid="pricing-cta">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
